import React, { useCallback, useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useToast } from "@totalsoft/rocket-ui";
import { useTranslation } from "react-i18next";
import PartnerAssociationModalEditComponent from "./PartnerAssociationModalEditComponent";
import { partnerAssociationModalStateVar } from "./variables";
import { Grid, Dialog, DialogContent, DialogTitle, IconButton } from "@mui/material";
import { useQueryWithSimpleErrorHandling } from 'hooks/apolloWrappers';
import { PARTNERS_FOR_ASSOCIATION_TO_HIERARCHY_QUERY } from "./queries";
import { useMutation } from "@apollo/client";
import { ASSOCIATE_PARTNER_TO_HIERARCHY_NODE, ASSOCIATE_PARTNERS_UNDER_HIERARCHY_NODE } from "./mutation";
import { Close, Check } from "@mui/icons-material";
import { clone } from "ramda";
import { getNodeAtPath, getParentPath } from "../../functions";
import { useSimpleError } from "hooks/apolloWrappers";

const PartnerAssociationModalEditContainer = ({ setExpandedItems, partnerAssociationModalState }) => {
  const { t } = useTranslation();
  const showToast = useToast();
  const [filterList, setFilterList] = useState([{ id: null, value: "" }]);
  const [pager, setPager] = useState({ page: 1, pageSize: 10 });
  const [displayedList, setDisplayedList] = useState([]);
  const [partnerIdListForMultipleSelection, setPartnerIdListForMultipleSelection] = useState([]);
  const showError = useSimpleError();

  const pagerUpdater = useCallback(
    (prop, value) => {
      return prop === "pageSize" ? { ...pager, [prop]: value, page: 1 } : { ...pager, [prop]: value };
    },
    [pager]
  );

  const onChangePage = useCallback(
    page => {
      setPager(pagerUpdater("page", page + 1));
    },
    [pagerUpdater]
  );

  const onChangeRowsPerPage = useCallback(
    pageSize => {
      setPager(pagerUpdater("pageSize", pageSize));
    },
    [pagerUpdater]
  );

  const handleClose = useCallback(() => {
    setPartnerIdListForMultipleSelection([]);
    partnerAssociationModalStateVar({ open: false });
  }, []);

  const { data: partnerData, loading: partnerDataLoading } = useQueryWithSimpleErrorHandling(
    PARTNERS_FOR_ASSOCIATION_TO_HIERARCHY_QUERY,
    {
      variables: { filterList: filterList }
    }
  );

  useEffect(() => {
    const startIndex = (pager.page - 1) * pager.pageSize;
    setDisplayedList(partnerData?.partnersForAssociationToHierarchy.slice(startIndex, startIndex + pager.pageSize));
  }, [partnerData, pager]);

  const [associatePartnerToHierarchyNode] = useMutation(ASSOCIATE_PARTNER_TO_HIERARCHY_NODE, {
    onError: error => {
      showError(error);
    },
    onCompleted: _ => {
      partnerAssociationModalStateVar({ open: false });
    },
    update: (cache, { data }) => {
      showToast(t("PartnerHierarchy.PartnerHierarchyDetailSaved"), "success", { autoClose: 3000 });
      cache.modify({
        id: `PartnerHierarchy:{"partnerHierarchyId":${data.associatePartnerToHierarchyNode.detail.partnerHierarchyId}}`,
        fields: {
          detailsTree: previous => {
            const newDetails = clone(previous);
            const detailToUpdate = getNodeAtPath(newDetails, data.associatePartnerToHierarchyNode.detail.path);
            detailToUpdate.data = { ...data.associatePartnerToHierarchyNode.detail };
            return newDetails;
          }
        }
      });
    }
  });

  const [associatePartnersUnderHierarchyNode] = useMutation(ASSOCIATE_PARTNERS_UNDER_HIERARCHY_NODE, {
    onError: error => {
      showError(error);
    },
    onCompleted: _ => {
      partnerAssociationModalStateVar({ open: false });
    },
    update: (cache, { data }) => {
      showToast(t("PartnerHierarchy.PartnerHierarchyDetailSaved"), "success", { autoClose: 3000 });
      cache.modify({
        id: `PartnerHierarchy:{"partnerHierarchyId":${data.associatePartnersUnderHierarchyNode.details[0].partnerHierarchyId}}`,
        fields: {
          detailsTree: previous => {
            const newDetails = clone(previous);
            const detailToUpdate = getNodeAtPath(
              newDetails,
              data.associatePartnersUnderHierarchyNode.details[0].path.split("/").slice(0, -2).join("/") + "/"
            );
            detailToUpdate.children = [
              ...detailToUpdate.children,
              ...data.associatePartnersUnderHierarchyNode.details.map(x => ({ data: x, children: [] }))
            ];
            return newDetails;
          }
        }
      });
      const pathToExpand = getParentPath(data.associatePartnersUnderHierarchyNode.details[0].path);
      setExpandedItems(prev => (!prev.includes(pathToExpand) ? [...prev, pathToExpand] : prev));
    }
  });

  const handleAssociate = useCallback(
    (partnerId, event) => {
      if (partnerAssociationModalState?.data?.singleSelection)
        associatePartnerToHierarchyNode({
          variables: {
            input: {
              partnerId: partnerId,
              nodeId: partnerAssociationModalState?.data?.nodeId
            }
          }
        });
      else
        setPartnerIdListForMultipleSelection(prev =>
          event === true ? [...prev, partnerId] : prev.filter(x => x !== partnerId)
        );
    },
    [
      associatePartnerToHierarchyNode,
      partnerAssociationModalState?.data?.nodeId,
      partnerAssociationModalState?.data?.singleSelection
    ]
  );

  const handleSelectConfirmation = useCallback(() => {
    associatePartnersUnderHierarchyNode({
      variables: {
        input: {
          partnerIds: partnerIdListForMultipleSelection,
          nodeId: partnerAssociationModalState?.data?.nodeId
        }
      }
    });
  }, [
    associatePartnersUnderHierarchyNode,
    partnerAssociationModalState?.data?.nodeId,
    partnerIdListForMultipleSelection
  ]);

  return (
    <Dialog
      id={`indicator_1`}
      open={partnerAssociationModalState.open}
      maxWidth="md"
      fullWidth
      TransitionProps={{ exit: false }}
    >
      <Grid container sx={{ p: 1 }}>
        <Grid item xs={!partnerAssociationModalState?.data?.singleSelection ? 10 : 11}>
          <DialogTitle color="primary" fontWeight="bold" variant="subtitle1" sx={{ pr: 0.5 }}>
            {partnerAssociationModalState?.data?.singleSelection
              ? t("PartnerHierarchy.PartnerAssociationModal.TitleSingleAssociation", {
                  nodeCategory: partnerAssociationModalState?.data?.nodeCategory,
                  nodeHierarchy: partnerAssociationModalState?.data?.nodeHierarchy
                })
              : t("PartnerHierarchy.PartnerAssociationModal.TitleMultipleAssociation", {
                  nodeCategory: partnerAssociationModalState?.data?.nodeCategory,
                  nodeHierarchy: partnerAssociationModalState?.data?.nodeHierarchy
                })}
          </DialogTitle>
        </Grid>
        {!partnerAssociationModalState?.data?.singleSelection && (
          <Grid item xs={1} sx={{ p: 1 }}>
            <IconButton
              onClick={handleSelectConfirmation}
              disabled={!partnerIdListForMultipleSelection.length}
              tooltip={t("PartnerHierarchy.PartnerAssociationModal.ButtonAssociate")}
            >
              <Check />
            </IconButton>
          </Grid>
        )}
        <Grid item xs={1} sx={{ p: 1 }}>
          <IconButton onClick={handleClose}>
            <Close />
          </IconButton>
        </Grid>
      </Grid>
      <DialogContent sx={{ pt: 0 }}>
        {partnerAssociationModalState.open && (
          <PartnerAssociationModalEditComponent
            filterList={filterList}
            setFilterList={setFilterList}
            partnerAssociationModalState={partnerAssociationModalState}
            partnerData={displayedList}
            partnerDataCount={partnerData?.partnersForAssociationToHierarchy?.length}
            partnerDataLoading={partnerDataLoading}
            handleAssociate={handleAssociate}
            pager={pager}
            onChangePage={onChangePage}
            onChangeRowsPerPage={onChangeRowsPerPage}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

PartnerAssociationModalEditContainer.propTypes = {
  setExpandedItems: PropTypes.func,
  partnerAssociationModalState: PropTypes.object
};

export default PartnerAssociationModalEditContainer;
